/**
 * PROJECTION UTM — les mètres dans lesquels un géomètre travaille.
 *
 * ⚠️⚠️ POURQUOI DU CÔTÉ CLIENT, ALORS QUE POSTGIS SAIT LE FAIRE. Parce que
 * l'affichage suit le CURSEUR : une lecture de position se rafraîchit à chaque
 * déplacement de souris, et un aller-retour au serveur par image mettrait le
 * registre à genoux pour afficher un couple de nombres. Les transformations
 * qui ENGAGENT (surfaces, distances officielles, géométries déposées) restent
 * faites par PostGIS, qui fait foi.
 *
 * ⚠️⚠️ ET C'EST PRÉCISÉMENT POURQUOI CE FICHIER EST CONFRONTÉ À POSTGIS DANS
 * SES ESSAIS, point par point, sur les DEUX zones du pays. Deux implémentations
 * d'une même transformation divergent au premier arrondi que l'une traite
 * autrement — et sur un cadastre, l'écart se lit en centimètres de limite. Le
 * banc `tests/utm.test.ts` compare cette formule à `ST_Transform` et refuse
 * au-delà du millimètre.
 *
 * ⚠️ LA GUINÉE EST À CHEVAL SUR DEUX FUSEAUX, et ce n'est pas un détail
 * d'affichage. Le méridien 12°W coupe le pays : Conakry est en zone 28
 * (EPSG:32628), Kankan et Nzérékoré en zone 29 (EPSG:32629). Mesuré le
 * 11/09/2026 par une autre session : 55 452 parcelles actives sur 59 545
 * (93,1 %) sont en zone 29. Afficher un X sans dire son fuseau donne un nombre
 * qui semble comparable à un autre et ne l'est pas — deux points distants de
 * 2 km de part et d'autre du méridien ont des X qui diffèrent de centaines de
 * kilomètres.
 */

/** Demi-grand axe et aplatissement de l'ellipsoïde WGS84 — les valeurs de la norme. */
const A = 6_378_137.0;
const F = 1 / 298.257223563;
const E2 = F * (2 - F);
const EP2 = E2 / (1 - E2);
/** Facteur d'échelle au méridien central, propre à UTM. */
const K0 = 0.9996;
const FAUX_EST = 500_000;

export interface PointUtm {
  /** Abscisse en mètres, avec le faux-est de 500 km. */
  x: number;
  /** Ordonnée en mètres depuis l'équateur (hémisphère nord). */
  y: number;
  zone: number;
  /** L'EPSG correspondant — celui que PostGIS emploierait. */
  epsg: number;
}

/**
 * Le fuseau UTM d'une longitude.
 *
 * ⚠️ MÊME FORMULE QUE `src/domaine/geometrie.ts` ET QUE LE SQL DES MODULES.
 * Une troisième version du même calcul rangerait une parcelle dans un fuseau
 * et sa surface dans un autre.
 */
export function zoneUtm(longitude: number): number {
  return Math.floor((longitude + 180) / 6) + 1;
}

/** EPSG d'un fuseau de l'hémisphère nord. */
export function epsgUtmNord(zone: number): number {
  if (!Number.isInteger(zone) || zone < 1 || zone > 60) {
    throw new RangeError(`Zone UTM hors bornes : ${zone}`);
  }
  return 32600 + zone;
}

/**
 * Projette un point WGS84 en UTM, hémisphère nord.
 *
 * ⚠️ LA ZONE SE DÉDUIT DE LA LONGITUDE, mais elle peut être IMPOSÉE. Un plan
 * cadastral qui chevauche le méridien doit rester dans UN fuseau, sinon ses
 * abscisses sautent de 500 km au milieu de la feuille. Le géomètre choisit
 * alors le fuseau de la section, et les points de l'autre côté sont projetés
 * dedans — c'est la pratique, et la formule le supporte sans dégradation sur
 * quelques degrés.
 */
export function versUtm(longitude: number, latitude: number, zoneImposee?: number): PointUtm {
  const zone = zoneImposee ?? zoneUtm(longitude);
  const lon0 = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180);
  const phi = latitude * (Math.PI / 180);
  const lam = longitude * (Math.PI / 180);

  const sinPhi = Math.sin(phi);
  const cosPhi = Math.cos(phi);
  const tanPhi = Math.tan(phi);

  const N = A / Math.sqrt(1 - E2 * sinPhi * sinPhi);
  const T = tanPhi * tanPhi;
  const C = EP2 * cosPhi * cosPhi;
  const Adiff = cosPhi * (lam - lon0);

  // Arc de méridien — développement en série usuel, ordre suffisant pour le
  // millimètre aux latitudes concernées.
  const M = A * (
    (1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 * E2 * E2) / 256) * phi
    - ((3 * E2) / 8 + (3 * E2 * E2) / 32 + (45 * E2 * E2 * E2) / 1024) * Math.sin(2 * phi)
    + ((15 * E2 * E2) / 256 + (45 * E2 * E2 * E2) / 1024) * Math.sin(4 * phi)
    - ((35 * E2 * E2 * E2) / 3072) * Math.sin(6 * phi)
  );

  const x = K0 * N * (
    Adiff
    + ((1 - T + C) * Adiff ** 3) / 6
    + ((5 - 18 * T + T * T + 72 * C - 58 * EP2) * Adiff ** 5) / 120
  ) + FAUX_EST;

  const y = K0 * (
    M + N * tanPhi * (
      (Adiff * Adiff) / 2
      + ((5 - T + 9 * C + 4 * C * C) * Adiff ** 4) / 24
      + ((61 - 58 * T + T * T + 600 * C - 330 * EP2) * Adiff ** 6) / 720
    )
  );

  return { x, y, zone, epsg: epsgUtmNord(zone) };
}

/**
 * L'INVERSE — d'un point UTM vers le WGS84 du registre.
 *
 * ⚠️⚠️ POURQUOI IL FALLAIT L'ÉCRIRE ICI, ET NULLE PART AILLEURS. L'import des
 * fichiers de géomètre (D.926) reçoit des mètres : un `.shp` en UTM 29N, un
 * `.dxf` sans système. Sans cette fonction, la tentation est d'en poser une
 * seconde dans le module d'import — et l'on aurait deux transformations
 * inverses l'une de l'autre par des chemins différents, qui divergeraient au
 * premier arrondi. `versUtm` et `depuisUtm` partagent A, E2, EP2, K0 et le
 * faux-est : c'est ce partage qui garantit que l'aller-retour ferme.
 *
 * ⚠️ AUCUN FAUX-NORD : comme `versUtm`, cette fonction est celle de
 * l'hémisphère NORD. La Guinée y est entière. Un fichier de l'hémisphère sud
 * est refusé PAR NOM à l'import, jamais lu ici comme s'il était au nord —
 * l'écart serait de 10 000 km, et il ne lèverait rien.
 *
 * Développement en série de Snyder, même ordre que l'aller : le banc
 * `tests/import-sig.test.ts` exige que l'aller-retour ferme sous le millimètre
 * sur les deux fuseaux du pays.
 */
export function depuisUtm(x: number, y: number, zone: number): { longitude: number; latitude: number } {
  if (!Number.isInteger(zone) || zone < 1 || zone > 60) {
    throw new RangeError(`Zone UTM hors bornes : ${zone}`);
  }
  const lon0 = ((zone - 1) * 6 - 180 + 3) * (Math.PI / 180);
  const xp = x - FAUX_EST;
  const M = y / K0;

  const mu = M / (A * (1 - E2 / 4 - (3 * E2 * E2) / 64 - (5 * E2 * E2 * E2) / 256));
  const e1 = (1 - Math.sqrt(1 - E2)) / (1 + Math.sqrt(1 - E2));

  const phi1 = mu
    + ((3 * e1) / 2 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu)
    + ((21 * e1 * e1) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu)
    + ((151 * e1 ** 3) / 96) * Math.sin(6 * mu)
    + ((1097 * e1 ** 4) / 512) * Math.sin(8 * mu);

  const sinPhi1 = Math.sin(phi1);
  const cosPhi1 = Math.cos(phi1);
  const tanPhi1 = Math.tan(phi1);

  const C1 = EP2 * cosPhi1 * cosPhi1;
  const T1 = tanPhi1 * tanPhi1;
  const N1 = A / Math.sqrt(1 - E2 * sinPhi1 * sinPhi1);
  const R1 = (A * (1 - E2)) / (1 - E2 * sinPhi1 * sinPhi1) ** 1.5;
  const D = xp / (N1 * K0);

  const phi = phi1 - ((N1 * tanPhi1) / R1) * (
    (D * D) / 2
    - ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * EP2) * D ** 4) / 24
    + ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * EP2 - 3 * C1 * C1) * D ** 6) / 720
  );

  const lam = lon0 + (
    D
    - ((1 + 2 * T1 + C1) * D ** 3) / 6
    + ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * EP2 + 24 * T1 * T1) * D ** 5) / 120
  ) / cosPhi1;

  return { longitude: lam * (180 / Math.PI), latitude: phi * (180 / Math.PI) };
}

/**
 * Le dénominateur d'échelle d'une carte — le « 1 / N » d'un plan.
 *
 * ⚠️ 0,28 mm PAR PIXEL, ET CE N'EST PAS UN CHOIX. C'est la taille de pixel
 * normalisée de l'OGC, celle que QGIS, ArcGIS et les services WMS emploient
 * tous. Prendre 96 dpi (0,2646 mm) donnerait une échelle plausible et
 * DIFFÉRENTE de celle que le même agent lit dans son autre logiciel — sur un
 * plan cadastral, deux échelles qui ne concordent pas sont pires qu'aucune.
 */
export function denominateurEchelle(metresParPixel: number): number {
  return metresParPixel / PIXEL_NORMALISE_M;
}

/**
 * Une longueur « ronde » pour la barre d'échelle, et sa largeur en pixels.
 *
 * ⚠️ LA BARRE MONTRE UNE DISTANCE LISIBLE, PAS UNE LARGEUR FIXE. Une barre de
 * 100 px annonçant « 37,4 m » ne se lit pas : l'œil ne sait pas reporter 37,4.
 * On choisit donc la distance dans la suite 1·2·5 et c'est la BARRE qui
 * s'ajuste — l'inverse de ce que fait un composant naïf.
 */
export function palierEchelle(metresParPixel: number, largeurMaxPx = 140): { metres: number; pixels: number } {
  const brut = metresParPixel * largeurMaxPx;
  const puissance = 10 ** Math.floor(Math.log10(Math.max(brut, 1e-6)));
  const mantisse = brut / puissance;
  const arrondie = mantisse >= 5 ? 5 : mantisse >= 2 ? 2 : 1;
  const metres = arrondie * puissance;
  return { metres, pixels: metres / metresParPixel };
}

/** Une distance en mètres, écrite comme un plan l'écrit. */
export function formaterDistance(metres: number): string {
  if (metres >= 1000) return `${(metres / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} km`;
  if (metres >= 1) return `${Math.round(metres).toLocaleString('fr-FR')} m`;
  return `${(metres * 100).toFixed(0)} cm`;
}

/**
 * LE PIXEL NORMALISÉ — 0,28 mm, convention OGC (WMS 1.3, « standardized
 * rendering pixel size »).
 *
 * ⚠️ IL VIVAIT EN LITTÉRAL DANS `denominateurEchelle`. Le sortir n'est pas de
 * la cosmétique : le verrou d'échelle doit inverser exactement ce que la barre
 * affiche, et deux fois `0.00028` dans deux fonctions, c'est une divergence qui
 * attend le premier correctif appliqué à une seule. Un seul nombre, deux
 * lecteurs.
 *
 * ⚠️ ET CE N'EST PAS LE PIXEL DE L'ÉCRAN. Un rapport d'échelle affiché suppose
 * une densité de référence ; le vrai écran de l'agent n'est pas à 90,7 ppp. La
 * barre GRAPHIQUE, elle, reste juste partout — c'est elle qui fait foi pour
 * reporter une distance. Un plan DÉLIVRÉ ne se mesure jamais sur l'écran : il
 * est composé par le service, à l'échelle vraie.
 */
export const PIXEL_NORMALISE_M = 0.00028;

/** Les échelles qu'un plan cadastral cite. Rien entre les crans : « 1/3 700 » ne se cite pas. */
export const ECHELLES_NORMALISEES = [200, 500, 1000, 2000, 5000, 10000, 25000] as const;

/**
 * LE ZOOM QUI REND EXACTEMENT UNE ÉCHELLE DEMANDÉE.
 *
 * On ne recalcule PAS les mètres par pixel depuis une formule scolaire
 * (`156543 · cos(lat) / 2^z`) : on part de la mesure déjà faite sur la carte,
 * au centre de la vue, et on se contente du rapport. Les mètres par pixel sont
 * proportionnels à `2^-zoom` ; il suffit donc d'ajouter le logarithme du
 * rapport. La latitude, la projection et le facteur d'échelle du fuseau sont
 * déjà DANS la mesure — les réintroduire ferait une seconde règle, fausse de
 * 1,4 % à Conakry si on l'écrivait à l'équateur.
 *
 * ⚠️ REND `null`, JAMAIS `NaN`. Un zoom `NaN` passé à `easeTo` ne lève rien :
 * la carte ne bouge pas, et le verrou paraît posé alors qu'il ne l'est pas.
 * Un refus explicite oblige l'appelant à le dire.
 */
export function zoomPourEchelle(
  zoomCourant: number,
  metresParPixelCourant: number,
  denominateur: number,
): number | null {
  if (!Number.isFinite(zoomCourant)) return null;
  if (!Number.isFinite(metresParPixelCourant) || metresParPixelCourant <= 0) return null;
  if (!Number.isFinite(denominateur) || denominateur <= 0) return null;
  const metresParPixelCible = denominateur * PIXEL_NORMALISE_M;
  return zoomCourant + Math.log2(metresParPixelCourant / metresParPixelCible);
}
